/**
 * The action bar's leaves (fix D / R5). Every button is its OWN memoised leaf
 * with its own subscription, so "offer draw" turning into "Offered · waiting"
 * costs one button render and not a bar render (UX spec §6a).
 *
 * The MODELS are M1a's toolbar tile models, unchanged — only the Views and the
 * container are new (R5). The phone still mounts `Toolbar` and the same models,
 * so the two faces of the same control can never drift apart.
 *
 * There is no sound button here (owner, 2026-10-06). `SoundTile` /
 * `useSoundTileModel` still exist for the phone `Toolbar`, and the preference
 * `rail/useSounds.ts` reads is untouched — the desktop bar simply no longer
 * offers the toggle.
 */
import React, {useCallback, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';

import {tcLabel} from '../../../pages/home/models/timeControls';
import {requestRematch, startNewGame} from '../../actions/useGameActions';
import {ConfirmDialog} from '../../../ui';
import {useAbortTileModel} from '../Toolbar/tiles/AbortTile.model';
import {useBerserkTileModel} from '../Toolbar/tiles/BerserkTile.model';
import {useDrawTileModel} from '../Toolbar/tiles/DrawTile.model';
import {useResignTileModel} from '../Toolbar/tiles/ResignTile.model';
import {useStopWatchingTileModel} from '../Toolbar/tiles/StopWatchingTile.model';
import {useThemeTileModel} from '../Toolbar/tiles/ThemeTile.model';
import {ABORT_CONFIRM_COPY} from '../Toolbar/tiles/AbortTile.View';
import {RESIGN_CONFIRM_COPY} from '../Toolbar/tiles/ResignTile.View';
import ActionButtonView from './ActionButton.View';
import IconButtonView from './IconButton.View';
import {selectArenaPauseButton, showArenaPauseButton} from './arenaPause';
import {selectCanPlayAgain, selectCanRematch} from './rematch';

export const DrawButton = React.memo(function DrawButton() {
  const {drawSent, showAbort, disabled, onPress} = useDrawTileModel();
  // old L129: hidden, not disabled, until the first move has been made
  if (showAbort) {
    return null;
  }
  return (
    <ActionButtonView
      testID="v2-action-draw"
      glyph="½"
      label={drawSent ? 'Offered' : 'Draw'}
      tone={drawSent ? 'active' : 'default'}
      disabled={disabled}
      onPress={onPress}
    />
  );
});

export const ResignButton = React.memo(function ResignButton() {
  const {gameOver, showAbort, resign} = useResignTileModel();
  const [asking, setAsking] = useState(false);
  const onCancel = useCallback(() => setAsking(false), []);
  const onConfirm = useCallback(() => {
    setAsking(false);
    resign();
  }, [resign]);

  // old L143: before the first move the legal action is Abort, not Resign
  if (showAbort) {
    return null;
  }
  return (
    <>
      <ActionButtonView
        testID="v2-action-resign"
        icon="resign"
        label="Resign"
        tone="danger"
        disabled={gameOver}
        onPress={() => setAsking(true)}
      />
      <ConfirmDialog
        testID="v2-confirm-resign"
        visible={asking}
        title={RESIGN_CONFIRM_COPY}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  );
});

/**
 * Fix T / T2 — "Pause after this game" / "Paused after this game", an arena
 * game only, LIVE face, right after Resign. A TOGGLE (see `arenaPause.ts`'s
 * header for the `resume()` read): paused calls `arenaResume`, not-paused
 * calls `arenaPause`. The Arena tab's own button (old `ArenaLobby`) and the
 * result card's toggle are untouched — this is the ONE new place it renders.
 */
export const ArenaPauseButton = React.memo(function ArenaPauseButton() {
  const input = useLiveGame(useShallow(selectArenaPauseButton));
  const arenaPause = useLiveGame(s => s.actions.arenaPause);
  const arenaResume = useLiveGame(s => s.actions.arenaResume);

  if (!showArenaPauseButton(input)) {
    return null;
  }
  return (
    <ActionButtonView
      testID="v2-action-arena-pause"
      label={input.paused ? 'Paused after this game' : 'Pause after this game'}
      tone={input.paused ? 'active' : 'default'}
      onPress={input.paused ? arenaResume : arenaPause}
    />
  );
});

export const AbortButton = React.memo(function AbortButton() {
  const {showAbort, onPress} = useAbortTileModel();
  const [asking, setAsking] = useState(false);
  const onCancel = useCallback(() => setAsking(false), []);
  const onConfirm = useCallback(() => {
    setAsking(false);
    onPress();
  }, [onPress]);

  if (!showAbort) {
    return null;
  }
  return (
    <>
      <ActionButtonView
        testID="v2-action-abort"
        icon="abort"
        label="Abort"
        tone="danger"
        onPress={() => setAsking(true)}
      />
      <ConfirmDialog
        testID="v2-confirm-abort"
        visible={asking}
        title={ABORT_CONFIRM_COPY}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  );
});

export const BerserkButton = React.memo(function BerserkButton() {
  const {showBerserk, onPress} = useBerserkTileModel();
  if (!showBerserk) {
    return null;
  }
  return (
    <ActionButtonView
      testID="v2-action-berserk"
      glyph="⚡"
      label="Berserk"
      tone="active"
      onPress={onPress}
    />
  );
});

export const StopWatchingButton = React.memo(function StopWatchingButton() {
  const {onPress} = useStopWatchingTileModel();
  return (
    <ActionButtonView
      testID="v2-action-stop-watching"
      icon="abort"
      label="Stop watching"
      onPress={onPress}
    />
  );
});

export const RematchButton = React.memo(function RematchButton() {
  const canRematch = useLiveGame(selectCanRematch);
  if (!canRematch) {
    return null;
  }
  return (
    <ActionButtonView
      testID="v2-action-rematch"
      icon="check"
      label="Rematch"
      tone="primary"
      onPress={requestRematch}
    />
  );
});

export const PlayAgainButton = React.memo(function PlayAgainButton() {
  const canPlayAgain = useLiveGame(selectCanPlayAgain);
  const tcMs = useLiveGame(s => s.game.tcMs);
  const incMs = useLiveGame(s => s.game.incMs);
  if (!canPlayAgain) {
    return null;
  }
  return (
    <ActionButtonView
      testID="v2-action-play-again"
      icon="plus"
      label={`New ${tcLabel(tcMs, incMs)}`}
      onPress={startNewGame}
    />
  );
});

export const ThemeButton = React.memo(function ThemeButton() {
  const {next, label, onPress} = useThemeTileModel();
  return (
    <IconButtonView
      testID="v2-action-theme"
      // the moon announces the dark theme; every light-ground theme reuses the sun
      icon={next === 'dark' ? 'moon' : 'sun'}
      label={label}
      onPress={onPress}
    />
  );
});
